import { Box, InputAdornment, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Path, UseFieldArrayRemove, useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import { AdditionalItemRendererProps } from '@/components/form/formAwareInputs/FormAwareInput.types';
import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import {
  FormAwareTypeaheadSelectInput,
  FormAwareTypeaheadSelectInputProps,
} from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { Trash01Icon } from '@/components/icons/Trash01Icon';
import { User01Icon } from '@/components/icons/User01Icon';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { getMaxLengthValidation } from '@/components/utils/validators';
import { MatchableKind } from '@/modules/aiSuggestions/aiSuggestions.types';
import { LocatableFormAwareInput } from '@/modules/aiSuggestions/components/forms/LocatableFormAwareInput';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import {
  useBeneficiaryLevelItems,
  useIsCurrentBeneficiaryEntity,
} from '@/modules/beneficiaries/beneficiaries.hooks';
import { AddNewAnythingModal } from '@/modules/common/AddNewAnythingModal/AddNewAnythingModal';
import { EntityMemberLevel } from '@/modules/content/tooltipContent/EntityMemberLevel';
import { EntityDocumentViewerDocument } from '@/modules/documents/components/HouseholdDocumentsViewer/graphql/EntityDocumentViewer.generated';
import {
  AccessParameterKind,
  AiSuggestionKind,
  EntityKind,
} from '@/types/schema';

import { useEntityDetailsContext } from '../contexts/entityDetails/entityDetails.context';
import { AccessParametersForm } from './AccessParametersForm';
import {
  AccessParameterRadioGroupKind,
  EntityBeneficiariesFormBeneficiary,
  EntityBeneficiariesFormNamespace,
  EntityBeneficiariesFormSecondNamespace,
  EntityBeneficiariesFormVariant,
} from './EntityBeneficiariesSubform.types';
import { PowerOfAppointmentForm } from './PowerOfAppointmentForm';

type BeneficiaryFormPaths = Path<EntityBeneficiariesFormBeneficiary>;

export function getBeneficiaryFormFieldName<T extends BeneficiaryFormPaths>(
  prefix: EntityBeneficiaryFormBodyV2Props['fieldName'],
  scheduledDistributionFormPath: T
): T {
  return `${prefix}.${scheduledDistributionFormPath}` as T;
}

export interface EntityBeneficiaryFormBodyV2Props {
  beneficiaryOptions: TypeaheadSelectInputOption<string>[];
  householdId: string;
  variant: EntityBeneficiariesFormVariant;
  splitScreen: boolean;
  additionalItemRendererProps?: AdditionalItemRendererProps;
  fieldName: `${EntityBeneficiariesFormNamespace}.${EntityBeneficiariesFormSecondNamespace}.${number}`;
  beneficiaryIndex: number;
  remove: UseFieldArrayRemove;
  /**
   * Override the suggestion kind used by the beneficiary dropdown's locator
   * icon. Subtrust sections pass SubtrustLifetimeBeneficiary /
   * SubtrustRemainderBeneficiary so the post-acceptance magnifier resolves
   * against the correct suggestion bucket.
   */
  suggestionKindOverride?: MatchableKind;
}

export function EntityBeneficiaryFormBodyV2({
  beneficiaryOptions,
  householdId,
  variant,
  splitScreen,
  additionalItemRendererProps,
  fieldName,
  beneficiaryIndex,
  remove,
  suggestionKindOverride,
}: EntityBeneficiaryFormBodyV2Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [newlyCreatedId, setNewlyCreatedId] = useState('');

  const isCurrentBeneficiaryEntity = useIsCurrentBeneficiaryEntity();

  const beneficiaryLevelItems = useBeneficiaryLevelItems();

  const { control, setShouldBlockNavigation, setFocus, setValue } =
    useFormContext<EntityBeneficiariesFormBeneficiary>();

  const canEdit = useCanEditHouseholdData();

  const { entityKind } = useEntityDetailsContext();
  const isRetirement = entityKind === EntityKind.RetirementPersonalAccount;

  const [notes, accessParameters, beneficiaryId] = useWatch({
    name: [
      getBeneficiaryFormFieldName(fieldName, 'notes'),
      getBeneficiaryFormFieldName(fieldName, 'beneficiaryFormAccessParameters'),
      getBeneficiaryFormFieldName(fieldName, 'beneficiaryId'),
    ],
    control,
  });

  const [notesAreExpanded, setNotesAreExpanded] = useState(Boolean(notes));

  function handleExpandNotes() {
    setNotesAreExpanded(true);
    // allow a loop for the notes to expand
    setTimeout(() => {
      setFocus(getBeneficiaryFormFieldName(fieldName, 'notes'));
    });
  }

  const { disabled: disabledFromContext } = useFormFieldsDisabled();

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // generally speaking, it's not required to select a beneficiary. however, we want to make sure that
  // a user isn't losing valuable data that they've entered because they forget to select the underlying beneficiary.
  // so, we mark the beneficiary dropdown as "required" if the user has entered data in what we deem to be
  // "high-value" fields in the subform.
  const beneficiarySelectionRequired = useMemo(() => {
    if (notes) return true;
    if (
      Object.values(accessParameters?.checkboxes ?? {}).filter((checked) => {
        return Boolean(checked);
      }).length
    )
      return true;
    return false;
  }, [accessParameters?.checkboxes, notes]);

  useEffect(() => {
    if (splitScreen && newlyCreatedId && !isOpen) {
      // Closing the modal after creating a new entity/individual/organization
      // should block navigation for the parent form.
      setShouldBlockNavigation(true);
      setNewlyCreatedId('');
    }
  }, [newlyCreatedId, setShouldBlockNavigation, isOpen, splitScreen]);

  // Effect to set the access parameter kind to partial for the percentOnly variant
  // since the percentOnly variant will always just be a single percent parameter
  useEffect(() => {
    if (variant === 'percentOnly') {
      setValue(
        `${getBeneficiaryFormFieldName(
          fieldName,
          'beneficiaryFormAccessParameters'
        )}._accessParameterKind`,
        AccessParameterRadioGroupKind.Partial
      );
      setValue(
        `${getBeneficiaryFormFieldName(
          fieldName,
          'beneficiaryFormAccessParameters'
        )}.amountsOrPercentages.0.kind`,
        AccessParameterKind.Percentage
      );
    }
  }, [fieldName, setValue, variant]);

  return (
    <>
      <AddNewAnythingModal
        addableTypes={['entity', 'individual', 'organization']}
        handleClose={handleClose}
        isOpen={isOpen}
        householdId={householdId}
        onAfterCreate={setNewlyCreatedId}
        addClientProfileModalProps={{
          ignoredQueryDocuments: [EntityDocumentViewerDocument],
        }}
        addClientOrganizationModalProps={{
          ignoredQueryDocuments: [EntityDocumentViewerDocument],
        }}
      />
      <Stack data-testid={`EntityBeneficiary-${beneficiaryIndex}`}>
        <FormLayoutRow sx={{ alignItems: 'center' }}>
          <FormLayoutItem width={isRetirement ? 12 : 8}>
            <LocatableFormAwareInput<
              EntityBeneficiariesFormBeneficiary,
              FormAwareTypeaheadSelectInputProps<EntityBeneficiariesFormBeneficiary>
            >
              FormAwareElement={FormAwareTypeaheadSelectInput}
              suggestionKind={
                suggestionKindOverride ?? AiSuggestionKind.BeneficiaryV2
              }
              control={control}
              label={isCurrentBeneficiaryEntity ? 'Individual' : 'Beneficiary'}
              fieldName={getBeneficiaryFormFieldName(
                fieldName,
                'beneficiaryId'
              )}
              addNewOption={{
                onClick: () => setIsOpen(true),
              }}
              options={beneficiaryOptions}
              required={beneficiarySelectionRequired}
              placeholder={`Search ${
                isCurrentBeneficiaryEntity ? 'individuals' : 'beneficiaries'
              }`}
              groupBy={(option) => option.groupName ?? ''}
              startAdornment={
                <InputAdornment position="start">
                  <User01Icon size={16} />
                </InputAdornment>
              }
              additionalItemRendererProps={additionalItemRendererProps}
            />
          </FormLayoutItem>
          {!isRetirement && (
            <FormLayoutItem width={4}>
              <FormAwareSelectInput<EntityBeneficiariesFormBeneficiary>
                control={control}
                label={isCurrentBeneficiaryEntity ? 'Precedence' : 'Ordering'}
                contextualHelp={
                  isCurrentBeneficiaryEntity ? undefined : <EntityMemberLevel />
                }
                fieldName={getBeneficiaryFormFieldName(fieldName, 'level')}
                options={beneficiaryLevelItems}
              />
            </FormLayoutItem>
          )}
        </FormLayoutRow>
        {variant === 'percentOnly' ? (
          <FormLayoutRow>
            <FormLayoutItem width={4}>
              <FormAwarePercentInput<EntityBeneficiariesFormBeneficiary>
                label="Distribution percentage"
                // these fields are typically not required, but in the case of a percentOnly variant (currently only GRATs),
                // we force collection of this field
                required
                fieldName={
                  // the percent-only variant only has one distribution, so we can safely zero-index
                  `${getBeneficiaryFormFieldName(
                    fieldName,
                    'beneficiaryFormAccessParameters'
                  )}.amountsOrPercentages.0.percentage` as const satisfies BeneficiaryFormPaths
                }
                control={control}
                isDecimalJSInput
                decimalScale={2}
              />
            </FormLayoutItem>
          </FormLayoutRow>
        ) : (
          <Stack spacing={1}>
            <AccessParametersForm
              beneficiaryFieldName={getBeneficiaryFormFieldName(
                fieldName,
                'beneficiaryFormAccessParameters'
              )}
              scheduledDistributionsFieldName={getBeneficiaryFormFieldName(
                fieldName,
                'beneficiaryFormScheduledDistributions'
              )}
              beneficiaryId={beneficiaryId}
            />
            {!isRetirement && (
              <PowerOfAppointmentForm
                fieldName={getBeneficiaryFormFieldName(
                  fieldName,
                  'beneficiaryFormPowerOfAppointment'
                )}
              />
            )}
          </Stack>
        )}
        <FormLayoutRow
          sx={{
            display: notesAreExpanded ? 'initial' : 'none',
            pt: notesAreExpanded ? 3 : 0,
          }}
        >
          <FormLayoutItem width={12}>
            <FormAwareTextInput<EntityBeneficiariesFormBeneficiary>
              label="Notes"
              multiline
              fieldName={getBeneficiaryFormFieldName(fieldName, 'notes')}
              control={control}
              rows={2}
              resizable
              validation={{
                maxLength: (value) =>
                  getMaxLengthValidation(2048)(value as string),
              }}
            />
          </FormLayoutItem>
        </FormLayoutRow>
        <Stack
          direction="row"
          justifyContent="space-between"
          pt={notesAreExpanded ? 0 : 3}
        >
          {!notesAreExpanded && canEdit && (
            <Button
              variant="transparent"
              size="sm"
              onClick={handleExpandNotes}
              startIcon={PlusIcon}
            >
              Add notes
            </Button>
          )}
          <Box />
          {!additionalItemRendererProps?.hideDeleteButton && (
            <Button
              disabled={disabledFromContext}
              variant="destructive-transparent"
              size="sm"
              onClick={() => {
                remove(beneficiaryIndex);
              }}
              startIcon={Trash01Icon}
              data-testid="button-remove-repeater-input-item"
            >
              {isCurrentBeneficiaryEntity
                ? 'Remove individual'
                : 'Remove beneficiary'}
            </Button>
          )}
        </Stack>
      </Stack>
    </>
  );
}
